import { Component, computed, MaybeRefOrGetter, ref, Ref, shallowRef, toValue } from 'vue';
import type { SlideDetailAdapterResultKey } from '@/common/types/slideDetailAdapter.types';
import { cloneDeep, isEqual, omit } from 'lodash-es';
import { useRoute } from 'vue-router';
import type {
  PageAnalysisDetailInfo,
  RumApplicationDetailInfo,
  TransactionDetailInfo,
  WasDetailInfo,
  TransactionTargetInfo,
  WebServerDetailInfo,
} from '@/application/utils/types';
import { BASE_MENU_VALUE } from '@/common/components/organisms/baseMenu/baseMenu.define';
import {
  ALERT_DETAIL,
  APPLICATION_DETAIL,
  CLOUD_SERVICE_DETAIL,
  CONFIG_DETAIL,
  CUSTOM_DETAIL,
  DATABASE_DETAIL,
  DETAIL_COMPONENTS,
  INFRA_DETAIL,
  KAFKA_DETAIL,
  KUBER_DETAIL,
  MESSAGE_QUEUE_DETAIL,
  SERVICE_DETAIL,
  TOOL_DETAIL,
  HOME_DETAIL,
  LOG_DETAIL,
  PA_KUBER_DETAIL,
  CONTAINER_DETAIL,
  AIOPS_DETAIL,
} from '@/common/define/slideDetail.define';
import type { BrowserErrorTrackingDetailInfo } from '@/application/components/browserErrorTrackingDetail/browserErrorTrackingDetail.types';
import type { AdditionForSqlDetail } from '@/database/components/sqlDetail/instanceSqlDetail.types';
import type { AdditionForSessionDetail } from '@/database/components/sessionDetail/instanceSessionDetail.types';
import type { AdditionForActiveQueryDetail } from '@/database/components/activeQueryDetail/activeQueryDetail.types';
import type { InstanceDetailInfo } from '@/database/components/instanceDetail/types';
import type { RolePermissionKeyType } from '@/config/views/permission/rolePermission/rolePermission.type';
import type { PagePerformanceMonitoringDetailInfo } from '@/application/views/pagePerformanceMonitoringView/pagePerformanceMonitoring.types';
import type { AjaxRequestHistoryDetailInfo } from '@/application/components/pageAnalysisDetail/ajaxRequestHistoryDetail/ajaxRequestHistory.types';
import type { AdditionType as ActiveTransactionDetailAddition } from '@/application/components/activeTransactionDetail/activeTransactionDetail.types';
import type { AgentDetailAddition } from '@/config/components/agentDetail/agentDetail.types';
import type { SessionAnalysisDetailInfo } from '@/application/views/userMonitoringView/userMonitoringView.types';
import type { SessionUserEventDetailInfo } from '@/application/components/sessionAnalysisDetail/sessionAnalysisDetail.types';
import type { CloudServiceDetailAddition } from '@/cloud/utils/types';
import type { AlertDetailInfo, EventAlertDetailInfo } from '@/alert/utils/types';
import type { InsightDetailResponseSeverity } from '@/api-client/aiops/model';
import { useRolePermission } from '@/common/permission/permission.utils';
import { ROLE_PERMISSION_KEY } from '@/common/define/rolePermission.define';
import { VIEW_MODE } from '@/common/define/common.define';
import type { KeyBrowserInfo } from '@/database/components/keyBrowserDetail/keyBrowserDetail.types';
import type { DatabaseObjectDetailInfo } from '@/database/components/objectDetail/objectDetail.types';
import type { CloudServiceAccountV8DtoCloud } from '@/api-client/cloud/model';
import type { DataCollectionAddition } from '@/config/views/management/data/components/dataCollectionSlide.setup';
import type { SlowQueryDetailParams } from '@/database/components/slowQuery/slowQuerySqlDetail.types';
import type { UrlMonitoringDetailInfo } from '@/application/views/urlMonitoring/types';
import type {
  LogDetailAddition,
  DetailedLogAnalysisAddition,
  KeywordLogDetailAddition,
} from '@/logs/utils/types';
import { getDetailKey } from '../utils/string.utils';
import { getFormattedTzTimeNow, getTzTimeNow } from '../utils/time.utils';

type KuberDetail = typeof KUBER_DETAIL;
type InfraDetail = typeof INFRA_DETAIL;
type MessageQueueDetail = typeof MESSAGE_QUEUE_DETAIL;
type ApplicationDetail = typeof APPLICATION_DETAIL;
type AlertDetail = typeof ALERT_DETAIL;
type ServiceDetail = typeof SERVICE_DETAIL;
type DatabaseDetail = typeof DATABASE_DETAIL;
type ToolDetail = typeof TOOL_DETAIL;
type ConfigDetail = typeof CONFIG_DETAIL;
type CloudServiceDetail = typeof CLOUD_SERVICE_DETAIL;
type CustomDetail = typeof CUSTOM_DETAIL;
type HomeDetail = typeof HOME_DETAIL;
type LogDetail = typeof LOG_DETAIL;
type PaKuberDetail = typeof PA_KUBER_DETAIL;
type ContainerDetail = typeof CONTAINER_DETAIL;
type AiopsDetail = typeof AIOPS_DETAIL;

export const TOTAL_DETAIL = {
  ...KUBER_DETAIL,
  ...INFRA_DETAIL,
  ...MESSAGE_QUEUE_DETAIL,
  ...APPLICATION_DETAIL,
  ...ALERT_DETAIL,
  ...DATABASE_DETAIL,
  ...SERVICE_DETAIL,
  ...TOOL_DETAIL,
  ...CONFIG_DETAIL,
  ...CLOUD_SERVICE_DETAIL,
  ...CUSTOM_DETAIL,
  ...HOME_DETAIL,
  ...LOG_DETAIL,
  ...PA_KUBER_DETAIL,
  ...CONTAINER_DETAIL,
  ...AIOPS_DETAIL,
};

export type DetailType =
  | KuberDetail[keyof KuberDetail]
  | InfraDetail[keyof InfraDetail]
  | MessageQueueDetail[keyof MessageQueueDetail]
  | ApplicationDetail[keyof ApplicationDetail]
  | AlertDetail[keyof AlertDetail]
  | ServiceDetail[keyof ServiceDetail]
  | DatabaseDetail[keyof DatabaseDetail]
  | ToolDetail[keyof ToolDetail]
  | ConfigDetail[keyof ConfigDetail]
  | CloudServiceDetail[keyof CloudServiceDetail]
  | CustomDetail[keyof CustomDetail]
  | HomeDetail[keyof HomeDetail]
  | LogDetail[keyof LogDetail]
  | PaKuberDetail[keyof PaKuberDetail]
  | ContainerDetail[keyof ContainerDetail]
  | AiopsDetail[keyof AiopsDetail];

export interface BaseTabPanelInfo {
  type: DetailType;
  name: string;
  title?: string;
  subTitle?: string; // 디테일 헤더 제목 보조 텍스트 — 있을 때만 노출
  init?: boolean; // history 초기화 여부
  state?: {
    selectedTab?: string;
    searchWord?: string;
    keepOpen?: boolean; // 슬라이드 창이 열려있을 때 동일 요소 클릭 시 슬라이드 창이 닫히는 기능 disable
    noKeepState?: boolean; // 헥사곤처럼 하나의 요소에서 탭을 나눠야할 경우 state를 유지하지 않기 위한 플래그
    openSlide?: boolean; // false면 탭 패널만 세팅하고 메인 슬라이드는 열지 않음
    [key: string]: any;
  };
  addition?: Record<string, any>;
  session?: Record<string, any>;
  rolePermissionKey?: RolePermissionKeyType; // type으로 권한 체크를 하였으나, 혹시 type으로 체크가 힘든 경우 따로 지정
  keepSplitTab?: boolean;
}

interface WasPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.WAS;
  was: WasDetailInfo;
}

interface WebServerPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.WEB_SERVER;
  webServer: WebServerDetailInfo;
}

interface TransactionPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.TRANSACTION;
  transaction: Omit<TransactionDetailInfo, 'traceKey'>;
}

interface ActiveTransactionPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.ACTIVE_TRANSACTION;
  addition: ActiveTransactionDetailAddition;
}

interface TransactionInfoPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.TRANSACTION_INFO;
  transaction: Omit<TransactionTargetInfo, 'endTime'>;
}

interface URLMonitoringInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.URL_MONITORING;
  addition: UrlMonitoringDetailInfo;
}
interface WebApplicationInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.WEB_APPLICATION;
  application: RumApplicationDetailInfo;
}

interface PageAnalysisInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.WEB_APPLICATION_PAGE_ANALYSIS;
  page: PageAnalysisDetailInfo;
}

interface BrowserErrorTrackingInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.BROWSER_ERROR_TRACKING;
  addition: BrowserErrorTrackingDetailInfo;
}

interface PagePerformanceMonitoringInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.PAGE_PERFORMANCE_MONITORING;
  addition: PagePerformanceMonitoringDetailInfo;
}

interface SessionAnalysisInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.USER_MONITORING_SESSION_ANALYSIS;
  addition: SessionAnalysisDetailInfo;
}

interface SessionReplayInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.USER_MONITORING_SESSION_REPLAY;
  addition: SessionAnalysisDetailInfo;
}

interface SessionUserEventInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.USER_MONITORING_USER_EVENT;
  addition: SessionUserEventDetailInfo;
}

interface AjaxRequestHistoryInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.AJAX_REQUEST_HISTORY;
  addition: AjaxRequestHistoryDetailInfo;
}

interface AlertPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.ALERT;
  addition: AlertDetailInfo;
}

interface EventAlertPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.EVENT_ALERT;
  addition: EventAlertDetailInfo;
}

interface SessionPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.SESSION;
  session: AdditionForSessionDetail;
}

interface SqlPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.SQL;
  addition: AdditionForSqlDetail;
}

export interface InstancePanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.INSTANCE;
  instance: InstanceDetailInfo;
}

export interface ClusterPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.CLUSTER;
  cluster: {
    instanceId: string;
    dbType?: string;
    databaseName?: string; // for singlestore
    fromTime?: string;
    toTime?: string;
  };
}

export interface ReplicationPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.REPLICATION;
  addition: {
    instanceId: string;
    replicationName: string;
    dbType?: string;
    fromTime?: string;
    toTime?: string;
  };
}

export interface ReplicationObjectPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.REPLICATION_OBJECT;
  addition: {
    instanceId: string;
    objectName: string;
    dbType?: string;
    fromTime: string;
    toTime: string;
    seriesColor?: Record<string, string>; // format : { objectName: color }
  };
}

export interface ShardedClusterPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.SHARDED_CLUSTER;
  instance: InstanceDetailInfo;
}

interface KeyBrowserPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.KEY_BROWSER;
  addition: KeyBrowserInfo;
}

export interface ObjectPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.OBJECT;
  addition: DatabaseObjectDetailInfo; // PG, MySQL만 지원
}

interface SlowQueryPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.SLOW_QUERY;
  addition: SlowQueryDetailParams;
}

interface ActiveQueryPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.ACTIVE_QUERY;
  addition: AdditionForActiveQueryDetail;
}

interface ContainerPanelInfo extends BaseTabPanelInfo {
  type: (typeof CONTAINER_DETAIL)[keyof typeof CONTAINER_DETAIL];
  container: string;
  addition?:
    | {
        server: 'k8s';
        podUid?: string;
        fromTime?: string;
        toTime?: string;
      }
    | {
        server: 'container_host';
        containerHostId: string;
        fromTime?: string;
        toTime?: string;
      };
}

interface HostDiskPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.DISK_DETAIL;
  host: string;
  addition?: {
    fileSystem?: string;
  };
}

interface GpuDetailPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.GPU_DETAIL;
  host: string;
  addition?: {
    uuid?: string;
    gpuUid?: string;
    gpuIndex?: number;
    gpuModel?: string;
    serverType?: string;
    selectedMigUid?: string;
    fromTime?: string;
    toTime?: string;
  };
}
interface HostNetworkPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.NETWORK_DETAIL;
  host: string;
}

interface HostProcessPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.PROCESS_DETAIL;
  host: string;
  pid: number;
  addition?: {
    fromTime?: string;
    toTime?: string;
  };
}

interface HostPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.HOST;
  host: string;
  addition?: {
    server?: string;
    fileSystem?: string;
    fromTime?: string;
    toTime?: string;
  };
}

interface NetworkDevicePanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.NETWORK_DEVICE;
  addition: {
    deviceId: string;
    networkDeviceName: string;
    fromTime?: string;
    toTime?: string;
  };
}
interface NetworkObjectPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.NETWORK_OBJECT;
  addition: {
    networkObjectId: string;
    fromTime?: string;
    toTime?: string;
  };
}

interface KafkaPanelDetail extends BaseTabPanelInfo {
  type: (typeof KAFKA_DETAIL)[keyof typeof KAFKA_DETAIL];
  addition: {
    topic?: string;
    instanceId: string;
    brokerTopicIds?: string[];
    instanceType: string; // Cluster, Broker, Topic
    entrySource?: {
      entry: string;
      entrySourceId: string;
    };
    fromTime?: string;
    toTime?: string;
  };
}

export interface CloudServicePanelDetail extends BaseTabPanelInfo {
  type: (typeof CLOUD_SERVICE_DETAIL)[keyof CloudServiceDetail];
  addition: CloudServiceDetailAddition;
}

export interface ScriptManagerHistoryPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.SCRIPT_DETAIL;
  addition: {
    executionId: string;
  };
}

export interface KuberDetailPanelInfo extends BaseTabPanelInfo {
  type: (typeof KUBER_DETAIL)[keyof KuberDetail];
  addition: {
    uid: string;
    collectTime?: string;
    k8sRoleType?: string;
    eventTime?: string;
  };
}

export interface EventDetailPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.EVENT;
  addition: {
    eventId: string;
    eventTime: string;
    kind: string;
    targetId: string;
    type: string;
  };
}

export interface HistoryManagementDetailPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.HISTORY_MANAGEMENT;
  addition: {
    clusterId: string;
    kind: string;
    uid: string;
    fromTime: string;
    toTime: string;
    podUid?: string;
    // killTime(축출 시각) 기준으로 연계 진입한 경우에만 세팅 — 조회기간(DateRangeSection) 가이드 툴팁 노출 여부 판별용.
    killTime?: string;
  };
}

interface ServiceDetailPanelInfo extends BaseTabPanelInfo {
  type: (typeof SERVICE_DETAIL)[keyof ServiceDetail];
  addition: {
    serviceId: string;
    fromTime?: string;
    toTime?: string;
  };
}

interface ParameterDetailPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.PARAMETER;
  addition: {
    dbType: string;
    instanceId: string;
    fromTime?: string;
    toTime?: string;
  };
}

interface BrokerPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.BROKER;
  addition: {
    dbType: string;
    instanceId: string;
    fromTime?: string;
    toTime?: string;
  };
}

interface QueryManagerDetailPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.QUERY;
  addition: {
    historyId: string;
  };
}

interface UsageTrendPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.USAGE_TREND;
}

interface DataCollectionPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.DATA_COLLECTION;
  addition: DataCollectionAddition;
}

interface ConfigAgentPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.AGENT;
  addition: AgentDetailAddition;
}

interface ConfigExecutionPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.EXECUTION;
  addition: {
    executionId: string;
  };
}

interface CustomPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.CUSTOM;
}

interface CloudAccountPanelInfo extends BaseTabPanelInfo {
  type: typeof CONFIG_DETAIL.CLOUD_ACCOUNT;
  addition: {
    cloud: CloudServiceAccountV8DtoCloud;
    accountId: string;
  };
}

interface AlertHistoryPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.ALERT_HISTORY;
  addition: {
    fromTime: string;
    toTime: string;
  };
}

interface LogPanelInfo extends BaseTabPanelInfo {
  type: typeof LOG_DETAIL.LOG;
  addition: LogDetailAddition;
}

interface DetailedLogAnalysisPanelInfo extends BaseTabPanelInfo {
  type: typeof LOG_DETAIL.DETAILED_LOG_ANALYSIS;
  addition: DetailedLogAnalysisAddition;
}
interface KeywordLogDetailPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.KEYWORD_LOG;
  addition: KeywordLogDetailAddition;
}

interface EolPolicyPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.EOL_EOS_POLICY;
  addition: {
    policyId: string;
    targetType: string;
  };
}

interface AiopsInsightPanelInfo extends BaseTabPanelInfo {
  type: typeof AIOPS_DETAIL.INSIGHT;
  addition: {
    rcaId?: string;
    collectTime?: string;
    /* 이하 2개는 슬라이드 탭 title 조립용 어댑터 입력 — InsightDetail 은 읽지 않는다. */
    ruleName?: string;
    labelName?: string;
  };
}

interface AiopsInsightRootCausePanelInfo extends BaseTabPanelInfo {
  type: typeof AIOPS_DETAIL.INSIGHT_ROOT_CAUSE;
  addition: {
    signalCode?: string;
    platform?: string;
    pCategory?: string;
    targetId?: string;
    targetName?: string;
    severity?: InsightDetailResponseSeverity;
    triggerTime?: string;
    collectTime?: string;
    rcaId?: string;
  };
}

interface ResourceInventoryServicePanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.RESOURCE_INVENTORY_SERVICE;
  service: { name?: string; uid?: string; type?: string; ports?: string }[];
  addition: {
    uid: string;
  };
}

interface ResourceInventoryIngressPanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.RESOURCE_INVENTORY_INGRESS;
  ingress: { name?: string; uid?: string; type?: string; ports?: string }[];
  addition: {
    uid: string;
  };
}

interface ResourceInventoryRoutePanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.RESOURCE_INVENTORY_ROUTE;
  route: { name?: string; uid?: string; type?: string; ports?: string }[];
  addition: {
    uid: string;
  };
}

interface ResourceInventoryVirtualServicePanelInfo extends BaseTabPanelInfo {
  type: typeof TOTAL_DETAIL.RESOURCE_INVENTORY_VIRTUAL_SERVICE;
  virtualService: {
    time?: string;
    workloadName?: string;
    workloadKind?: string;
    virtualServices?: {
      name?: string;
      uid?: string;
      hosts?: string;
      paths?: string;
      weights?: string;
    }[];
  };
  addition: {
    uid: string;
  };
}

/* prettier-ignore */
export type PanelInfo<Type extends DetailType> =
  Type extends typeof TOTAL_DETAIL.WAS ? WasPanelInfo :
  Type extends typeof TOTAL_DETAIL.WEB_SERVER ? WebServerPanelInfo :
  Type extends typeof TOTAL_DETAIL.TRANSACTION ? TransactionPanelInfo :
  Type extends typeof TOTAL_DETAIL.ACTIVE_TRANSACTION ? ActiveTransactionPanelInfo :
  Type extends typeof TOTAL_DETAIL.TRANSACTION_INFO ? TransactionInfoPanelInfo :
  Type extends typeof TOTAL_DETAIL.WEB_APPLICATION ? WebApplicationInfo :
  Type extends typeof TOTAL_DETAIL.WEB_APPLICATION_PAGE_ANALYSIS ? PageAnalysisInfo :
  Type extends typeof TOTAL_DETAIL.AJAX_REQUEST_HISTORY ? AjaxRequestHistoryInfo :
  Type extends typeof TOTAL_DETAIL.BROWSER_ERROR_TRACKING ? BrowserErrorTrackingInfo :
  Type extends typeof TOTAL_DETAIL.PAGE_PERFORMANCE_MONITORING ? PagePerformanceMonitoringInfo :
  Type extends typeof TOTAL_DETAIL.USER_MONITORING_SESSION_ANALYSIS ? SessionAnalysisInfo :
  Type extends typeof TOTAL_DETAIL.USER_MONITORING_SESSION_REPLAY ? SessionReplayInfo :
  Type extends typeof TOTAL_DETAIL.USER_MONITORING_USER_EVENT ? SessionUserEventInfo :
  Type extends typeof TOTAL_DETAIL.URL_MONITORING ? URLMonitoringInfo :
  Type extends typeof TOTAL_DETAIL.ALERT ? AlertPanelInfo :
  Type extends typeof TOTAL_DETAIL.EVENT_ALERT ? EventAlertPanelInfo :
  Type extends typeof TOTAL_DETAIL.SESSION ? SessionPanelInfo :
  Type extends typeof TOTAL_DETAIL.SQL ? SqlPanelInfo :
  Type extends typeof TOTAL_DETAIL.SLOW_QUERY ? SlowQueryPanelInfo :
  Type extends typeof TOTAL_DETAIL.ACTIVE_QUERY ? ActiveQueryPanelInfo :
  Type extends typeof TOTAL_DETAIL.BROKER ? BrokerPanelInfo :
  Type extends typeof TOTAL_DETAIL.PARAMETER ? ParameterDetailPanelInfo :
  Type extends typeof TOTAL_DETAIL.INSTANCE ? InstancePanelInfo :
  Type extends typeof TOTAL_DETAIL.CLUSTER ? ClusterPanelInfo :
  Type extends typeof TOTAL_DETAIL.REPLICATION ? ReplicationPanelInfo :
  Type extends typeof TOTAL_DETAIL.REPLICATION_OBJECT ? ReplicationObjectPanelInfo :
  Type extends typeof TOTAL_DETAIL.SHARDED_CLUSTER ? ShardedClusterPanelInfo:
  Type extends typeof TOTAL_DETAIL.KEY_BROWSER ? KeyBrowserPanelInfo :
  Type extends typeof TOTAL_DETAIL.OBJECT ? ObjectPanelInfo :
  Type extends typeof TOTAL_DETAIL.DISK_DETAIL ? HostDiskPanelInfo :
  Type extends typeof TOTAL_DETAIL.GPU_DETAIL ? GpuDetailPanelInfo :
  Type extends typeof TOTAL_DETAIL.NETWORK_DETAIL ? HostNetworkPanelInfo :
  Type extends typeof TOTAL_DETAIL.PROCESS_DETAIL ? HostProcessPanelInfo :
  Type extends typeof TOTAL_DETAIL.HOST ? HostPanelInfo :
  Type extends typeof TOTAL_DETAIL.NETWORK_DEVICE ? NetworkDevicePanelInfo :
  Type extends typeof TOTAL_DETAIL.NETWORK_OBJECT ? NetworkObjectPanelInfo :
  Type extends typeof TOTAL_DETAIL.SCRIPT_DETAIL ? ScriptManagerHistoryPanelInfo :
  Type extends typeof TOTAL_DETAIL.RESOURCE_INVENTORY_SERVICE ? ResourceInventoryServicePanelInfo :
  Type extends typeof TOTAL_DETAIL.RESOURCE_INVENTORY_INGRESS ? ResourceInventoryIngressPanelInfo :
  Type extends typeof TOTAL_DETAIL.RESOURCE_INVENTORY_ROUTE ? ResourceInventoryRoutePanelInfo :
  Type extends typeof TOTAL_DETAIL.RESOURCE_INVENTORY_VIRTUAL_SERVICE ? ResourceInventoryVirtualServicePanelInfo :
  Type extends (typeof TOTAL_DETAIL)[keyof KuberDetail] ? KuberDetailPanelInfo :
  Type extends (typeof TOTAL_DETAIL)[keyof ServiceDetail] ? ServiceDetailPanelInfo :
  Type extends (typeof TOTAL_DETAIL)[keyof ToolDetail] ? QueryManagerDetailPanelInfo :
  Type extends typeof TOTAL_DETAIL.AGENT ? ConfigAgentPanelInfo :
  Type extends typeof TOTAL_DETAIL.EXECUTION ? ConfigExecutionPanelInfo :
  Type extends typeof TOTAL_DETAIL.USAGE_TREND ? UsageTrendPanelInfo :
  Type extends typeof TOTAL_DETAIL.DATA_COLLECTION ? DataCollectionPanelInfo :
  Type extends (typeof TOTAL_DETAIL)[keyof MessageQueueDetail] ? KafkaPanelDetail :
  Type extends (typeof TOTAL_DETAIL)[keyof CloudServiceDetail] ? CloudServicePanelDetail :
  Type extends typeof TOTAL_DETAIL.CUSTOM ? CustomPanelInfo :
  Type extends typeof TOTAL_DETAIL.ALERT_HISTORY ? AlertHistoryPanelInfo :
  Type extends typeof TOTAL_DETAIL.CLOUD_ACCOUNT ? CloudAccountPanelInfo :
  Type extends typeof LOG_DETAIL.LOG ? LogPanelInfo :
  Type extends typeof LOG_DETAIL.DETAILED_LOG_ANALYSIS ? DetailedLogAnalysisPanelInfo :
  Type extends typeof TOTAL_DETAIL.KEYWORD_LOG ? KeywordLogDetailPanelInfo :
  Type extends typeof TOTAL_DETAIL.EVENT ?  EventDetailPanelInfo:
  Type extends typeof TOTAL_DETAIL.HISTORY_MANAGEMENT ? HistoryManagementDetailPanelInfo :
  Type extends typeof TOTAL_DETAIL.EOL_EOS_POLICY ? EolPolicyPanelInfo :
  Type extends (typeof TOTAL_DETAIL)[keyof ContainerDetail] ? ContainerPanelInfo :
  Type extends typeof AIOPS_DETAIL.INSIGHT ? AiopsInsightPanelInfo :
  Type extends typeof AIOPS_DETAIL.INSIGHT_ROOT_CAUSE ? AiopsInsightRootCausePanelInfo :
  never;

export type AddTabPanelInfo = PanelInfo<DetailType>;

// value값으로 탭 구분.
export type SlideTabPanelInfo = AddTabPanelInfo & {
  value: number;
  date: string;
  splitInfo?: AddTabPanelInfo;
};

type CustomSlideTabPanelInfo<T extends {} = {}> = {
  type: typeof TOTAL_DETAIL.CUSTOM;
  props: T;
  subTitle?: string;
};

const detailTypeCheckList: string[] = [
  'Node',
  'Namespace',
  'Pod',
  'Deployment',
  'DaemonSet',
  'StatefulSet',
  'ReplicaSet',
  'Service',
];
export const isDetailPage = (type: string): boolean => detailTypeCheckList.includes(type);

export const convertK8sTabPanelType = (str: string): KuberDetailPanelInfo['type'] => {
  const type: Record<string, KuberDetailPanelInfo['type']> = {
    nodelist: TOTAL_DETAIL.NODE_LIST,
    namespacelist: TOTAL_DETAIL.NAMESPACE_LIST,
    podlist: TOTAL_DETAIL.POD_LIST,
    serviceobjectlist: TOTAL_DETAIL.SERVICE_OBJECT_LIST,
    ingresslist: TOTAL_DETAIL.INGRESS_LIST,
    persistentvolumeclaimlist: TOTAL_DETAIL.PERSISTENT_VOLUMES_CLAIMS_LIST,
    persistentvolumelist: TOTAL_DETAIL.PERSISTENT_VOLUMES_LIST,
    storageclasslist: TOTAL_DETAIL.STORAGE_CLASSES_LIST,
    replicaset: TOTAL_DETAIL.REPLICA_SET,
    daemonset: TOTAL_DETAIL.DAEMON_SET,
    statefulset: TOTAL_DETAIL.STATEFUL_SET,
    deployment: TOTAL_DETAIL.DEPLOYMENT,
    job: TOTAL_DETAIL.JOB,
    node: TOTAL_DETAIL.NODE,
    namespace: TOTAL_DETAIL.NAMESPACE,
    pod: TOTAL_DETAIL.POD,
    ingress: TOTAL_DETAIL.INGRESS,
    service: TOTAL_DETAIL.SERVICE,
    cronjob: TOTAL_DETAIL.CRON_JOB,
    hpa: TOTAL_DETAIL.HPA,
    rolesbindings: TOTAL_DETAIL.ROLES_BINDINGS,
    serviceaccounts: TOTAL_DETAIL.SERVICE_ACCOUNTS,
    roles: TOTAL_DETAIL.ROLES,
    configmaps: TOTAL_DETAIL.CONFIG_MAPS,
    persistentvolumesclaims: TOTAL_DETAIL.PERSISTENT_VOLUMES_CLAIMS,
    storageclasses: TOTAL_DETAIL.STORAGE_CLASSES,
    clusterroles: TOTAL_DETAIL.CLUSTER_ROLES,
    clusterrolesbindings: TOTAL_DETAIL.CLUSTER_ROLES_BINDINGS,
    persistentvolumes: TOTAL_DETAIL.PERSISTENT_VOLUMES,
  };

  return type[getDetailKey(str)] ?? TOTAL_DETAIL.NODE;
};

export const DetailTypeToPermissionKey: Partial<Record<DetailType, RolePermissionKeyType>> = {
  [TOTAL_DETAIL.NODE_LIST]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_INFRASTRUCTURE,
  [TOTAL_DETAIL.NAMESPACE_LIST]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_INFRASTRUCTURE,
  [TOTAL_DETAIL.POD_LIST]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_INFRASTRUCTURE,
  [TOTAL_DETAIL.SERVICE_OBJECT_LIST]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_NETWORK,
  [TOTAL_DETAIL.INGRESS_LIST]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_NETWORK,
  [TOTAL_DETAIL.PERSISTENT_VOLUMES_CLAIMS_LIST]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_STORAGE,
  [TOTAL_DETAIL.PERSISTENT_VOLUMES_LIST]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_STORAGE,
  [TOTAL_DETAIL.STORAGE_CLASSES_LIST]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_STORAGE,
  [TOTAL_DETAIL.NAMESPACE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_INFRASTRUCTURE,
  [TOTAL_DETAIL.POD]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_INFRASTRUCTURE,
  [TOTAL_DETAIL.NODE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_INFRASTRUCTURE,
  [TOTAL_DETAIL.CRON_JOB]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_WORKLOAD,
  [TOTAL_DETAIL.OWNER_REFERENCES]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_WORKLOAD,
  [TOTAL_DETAIL.DEPLOYMENT]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_WORKLOAD,
  [TOTAL_DETAIL.STATEFUL_SET]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_WORKLOAD,
  [TOTAL_DETAIL.DAEMON_SET]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_WORKLOAD,
  [TOTAL_DETAIL.HPA]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_SCALING,
  [TOTAL_DETAIL.JOB]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_WORKLOAD,
  [TOTAL_DETAIL.REPLICA_SET]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_WORKLOAD,
  [TOTAL_DETAIL.SERVICE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_NETWORK,
  [TOTAL_DETAIL.VIRTUAL_SERVICE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_NETWORK,
  [TOTAL_DETAIL.ROLES_BINDINGS]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_ACCESS_CONTROL,
  [TOTAL_DETAIL.SERVICE_ACCOUNTS]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_ACCESS_CONTROL,
  [TOTAL_DETAIL.ROLES]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_ACCESS_CONTROL,
  [TOTAL_DETAIL.INGRESS]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_NETWORK,
  [TOTAL_DETAIL.OPENSHIFT_ROUTE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_NETWORK,
  [TOTAL_DETAIL.RESOURCE_INVENTORY_SERVICE]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_INVENTORY,
  [TOTAL_DETAIL.RESOURCE_INVENTORY_INGRESS]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_INVENTORY,
  [TOTAL_DETAIL.RESOURCE_INVENTORY_ROUTE]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_INVENTORY,
  [TOTAL_DETAIL.RESOURCE_INVENTORY_VIRTUAL_SERVICE]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_INVENTORY,
  [TOTAL_DETAIL.CONFIG_MAPS]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_CONFIGURATION,
  [TOTAL_DETAIL.PERSISTENT_VOLUMES_CLAIMS]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_STORAGE,
  [TOTAL_DETAIL.STORAGE_CLASSES]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_STORAGE,
  [TOTAL_DETAIL.CLUSTER_ROLES]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_ACCESS_CONTROL,
  [TOTAL_DETAIL.CLUSTER_ROLES_BINDINGS]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_ACCESS_CONTROL,
  [TOTAL_DETAIL.PERSISTENT_VOLUMES]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_STORAGE,
  [TOTAL_DETAIL.EVENT]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_ANALYSIS,
  [TOTAL_DETAIL.HISTORY_MANAGEMENT]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_ANALYSIS,
  [TOTAL_DETAIL.HOST]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_INFRASTRUCTURE_HOST,
  [TOTAL_DETAIL.NETWORK_DEVICE]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_INFRASTRUCTURE_NETWORK_DEVICE,
  [TOTAL_DETAIL.PROCESS_DETAIL]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_INFRASTRUCTURE_HOST,
  [TOTAL_DETAIL.NETWORK_DETAIL]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_INFRASTRUCTURE_HOST,
  [TOTAL_DETAIL.DISK_DETAIL]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_INFRASTRUCTURE_HOST,
  [TOTAL_DETAIL.GPU_DETAIL]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_GPU_MONITORING,
  [TOTAL_DETAIL.SCRIPT_DETAIL]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_TOOL_SCRIPT_MANAGER_HISTORY,
  [TOTAL_DETAIL.WAS]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_WAS,
  [TOTAL_DETAIL.WEB_SERVER]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_WEB_SERVER,
  [TOTAL_DETAIL.TRANSACTION]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_TRANSACTION,
  [TOTAL_DETAIL.TRANSACTION_INFO]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_TRANSACTION,
  [TOTAL_DETAIL.ACTIVE_TRANSACTION]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_TRANSACTION,
  [TOTAL_DETAIL.WEB_APPLICATION]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_WEB_APPLICATION,
  [TOTAL_DETAIL.WEB_APPLICATION_PAGE_ANALYSIS]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_WEB_APPLICATION,
  [TOTAL_DETAIL.AJAX_REQUEST_HISTORY]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_WEB_APPLICATION,
  [TOTAL_DETAIL.PAGE_PERFORMANCE_MONITORING]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_PAGE_PERFORMANCE_MONITORING,
  [TOTAL_DETAIL.USER_MONITORING_SESSION_ANALYSIS]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_USER_MONITORING,
  [TOTAL_DETAIL.USER_MONITORING_SESSION_REPLAY]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_USER_MONITORING,
  [TOTAL_DETAIL.USER_MONITORING_USER_EVENT]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_USER_MONITORING,
  [TOTAL_DETAIL.URL_MONITORING]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_URL_MONITORING,
  [TOTAL_DETAIL.NETWORK_OBJECT]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_INFRASTRUCTURE_NETWORK_PERFORMANCE_MONITORING,
  [TOTAL_DETAIL.BROWSER_ERROR_TRACKING]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_BROWSER_ERROR_TRACKING,
  [TOTAL_DETAIL.ALERT]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_ALERT_LIST,
  [TOTAL_DETAIL.EVENT_ALERT]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_ALERT_EVENT_ALERT_LIST,
  /* AIOps Insight 슬라이드 — AIOps 권한 행의 detailSlide 토글로 접근 제어. */
  [TOTAL_DETAIL.INSIGHT]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_AIOPS,
  [TOTAL_DETAIL.INSIGHT_ROOT_CAUSE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_AIOPS,
  [TOTAL_DETAIL.SERVICE_LIST]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_BUSINESS_LIST,
  [TOTAL_DETAIL.INSTANCE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_INSTANCE,
  [TOTAL_DETAIL.CLUSTER]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_INSTANCE,
  [TOTAL_DETAIL.SHARDED_CLUSTER]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_INSTANCE,
  [TOTAL_DETAIL.REPLICATION]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_INSTANCE,
  [TOTAL_DETAIL.REPLICATION_OBJECT]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_INSTANCE,
  [TOTAL_DETAIL.SESSION]: {
    [VIEW_MODE.EXEMONE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_INSTANCE,
  },
  [TOTAL_DETAIL.SQL]: {
    [VIEW_MODE.EXEMONE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_INSTANCE,
  },
  [TOTAL_DETAIL.PARAMETER]: {
    [VIEW_MODE.EXEMONE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_INSTANCE,
  },
  [TOTAL_DETAIL.BROKER]: {
    [VIEW_MODE.EXEMONE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_INSTANCE,
  },
  [TOTAL_DETAIL.KEY_BROWSER]: {
    [VIEW_MODE.EXEMONE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_INSTANCE,
  },
  [TOTAL_DETAIL.OBJECT]: {
    [VIEW_MODE.EXEMONE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_ANALYSIS,
  },
  [TOTAL_DETAIL.SLOW_QUERY]: {
    [VIEW_MODE.EXEMONE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_INSTANCE,
  },
  [TOTAL_DETAIL.ACTIVE_QUERY]: {
    [VIEW_MODE.EXEMONE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_INSTANCE,
  },
  [TOTAL_DETAIL.QUERY]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_TOOL_QUERY_MANAGER_HISTORY,
  [TOTAL_DETAIL.USAGE_TREND]: ROLE_PERMISSION_KEY.SETTING.SETTING_MANAGEMENT_DATA_RETENTION,
  [TOTAL_DETAIL.AGENT]: ROLE_PERMISSION_KEY.SETTING.SETTING_MANAGEMENT_AGENT, // NOTE: Agent 는 무조건 마스터 권한
  [TOTAL_DETAIL.EXECUTION]: ROLE_PERMISSION_KEY.SETTING.SETTING_MANAGEMENT_AGENT,
  [TOTAL_DETAIL.CLOUD_ACCOUNT]: ROLE_PERMISSION_KEY.SETTING.SETTING_PLATFORM_CLOUD_ACCOUNT,
  [TOTAL_DETAIL.DATA_COLLECTION]:
    ROLE_PERMISSION_KEY.SETTING.SETTING_MANAGEMENT_DATA_METRIC_COLLECTION_CYCLE_MANAGEMENT,

  [TOTAL_DETAIL.KAFKA_CLUSTER]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_MESSAGEQUEUE_KAFKA,
  [TOTAL_DETAIL.KAFKA_BROKER]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_MESSAGEQUEUE_KAFKA,
  [TOTAL_DETAIL.KAFKA_TOPIC]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_MESSAGEQUEUE_KAFKA,
  [TOTAL_DETAIL.AWS_EC2]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_AWS_EC2,
  [TOTAL_DETAIL.AWS_LAMBDA]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_AWS_LAMBDA,
  [TOTAL_DETAIL.AWS_S3]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_AWS_S3,
  [TOTAL_DETAIL.AWS_DOCUMENTDB]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_AWS_DOCUMENT_DB,
  [TOTAL_DETAIL.AWS_DYNAMODB]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_AWS_DYNAMO_DB,
  [TOTAL_DETAIL.AWS_ELASTICACHE_CLUSTER]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_AWS_ELASTI_CACHE,
  [TOTAL_DETAIL.AWS_ELASTICACHE_SERVERLESS]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_AWS_ELASTI_CACHE,
  [TOTAL_DETAIL.AWS_ELASTICACHE_SHARD]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_AWS_ELASTI_CACHE,
  [TOTAL_DETAIL.AWS_ELASTICACHE_NODE]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_AWS_ELASTI_CACHE,
  [TOTAL_DETAIL.AWS_RDS]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_AWS_RDS,
  [TOTAL_DETAIL.AWS_REDSHIFT]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_AWS_REDSHIFT,
  [TOTAL_DETAIL.AWS_DMS_TASK]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_AWS_DATABASE_MIGRATION,
  [TOTAL_DETAIL.AWS_DMS_REPLINSTANCE]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_AWS_DATABASE_MIGRATION,
  [TOTAL_DETAIL.ALERT_HISTORY]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_ALERT_LIST,
  [TOTAL_DETAIL.CURRENT_ALERT]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_ALERT_LIST,
  [TOTAL_DETAIL.CLOUD_COMPUTING]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_COMPUTING,
  [TOTAL_DETAIL.CLOUD_STORAGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_STORAGE,
  [TOTAL_DETAIL.CLOUD_DATABASE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_DATABASE,
  [TOTAL_DETAIL.LOG]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_LOGS_LOGGING_TREND,
  [TOTAL_DETAIL.DETAILED_LOG_ANALYSIS]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_LOGS_LOGGING_TREND,
  [TOTAL_DETAIL.KEYWORD_LOG]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_LOGS_LOGGING,
  [TOTAL_DETAIL.EOL_EOS_POLICY]: ROLE_PERMISSION_KEY.SETTING.SETTING_ASSET_EOL_EOS_POLICY,
  [TOTAL_DETAIL.CONTAINER_HOST]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_INFRASTRUCTURE_CONTAINER,
  [TOTAL_DETAIL.K8S_CONTAINER]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_INFRASTRUCTURE,
} as const;

export const CurrentDashboardDetailPermission: Record<string, RolePermissionKeyType> = {
  [BASE_MENU_VALUE.INFRASTRUCTURE]:
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_INFRASTRUCTURE_DASHBOARD,
  [BASE_MENU_VALUE.KUBERNETES]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_DASHBOARD,
  [BASE_MENU_VALUE.APPLICATION]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_DASHBOARD,
  [BASE_MENU_VALUE.DATABASE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_DASHBOARD,
  [BASE_MENU_VALUE.BUSINESS]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_BUSINESS_DASHBOARD,
  [BASE_MENU_VALUE.LOGS]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_LOGS_DASHBOARD,
  [BASE_MENU_VALUE.ALERT]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_ALERT_DASHBOARD,
  [BASE_MENU_VALUE.ORACLE]: {
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_ORACLE_DASHBOARD,
  },
  [BASE_MENU_VALUE.POSTGRESQL]: {
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_POSTGRESQL_DASHBOARD,
  },
  [BASE_MENU_VALUE.MYSQL]: {
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_MYSQL_DASHBOARD,
  },
  [BASE_MENU_VALUE.SQLSERVER]: {
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_SQLSERVER_DASHBOARD,
  },
  [BASE_MENU_VALUE.REDIS]: {
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_REDIS_DASHBOARD,
  },
  [BASE_MENU_VALUE.CUBRID]: {
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CUBRID_DASHBOARD,
  },
  [BASE_MENU_VALUE.MONGODB]: {
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_MONGO_DASHBOARD,
  },
  [BASE_MENU_VALUE.ALTIBASE]: {
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_ALTIBASE_DASHBOARD,
  },
  [BASE_MENU_VALUE.TIBERO]: {
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_TIBERO_DASHBOARD,
  },
  [BASE_MENU_VALUE.CLICKHOUSE]: {
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLICKHOUSE_DASHBOARD,
  },
  [BASE_MENU_VALUE.REDSHIFT]: {
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_REDSHIFT_DASHBOARD,
  },
  [BASE_MENU_VALUE.SINGLESTORE]: {
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_SINGLESTORE_DASHBOARD,
  },
} as const;

const useDetailSlidePermission = () => {
  const route = useRoute();

  const { isPermissionDenied } = useRolePermission();

  const isDetailSlidePermissionDenied = ({
    rolePermissionKey,
    type,
    showError = true,
  }: {
    rolePermissionKey?: RolePermissionKeyType;
    type: DetailType;
    showError?: boolean;
  }) => {
    return (
      (route.query.rootMenu &&
        isPermissionDenied({
          type: 'detailSlide',
          rolePermissionKey: CurrentDashboardDetailPermission[`${route.query.rootMenu}`],
          showError,
        })) ||
      isPermissionDenied({
        type: 'detailSlide',
        rolePermissionKey: rolePermissionKey || DetailTypeToPermissionKey[type],
        showError,
      })
    );
  };

  return {
    isDetailSlidePermissionDenied,
  };
};

export const useSplitSlideDetail = (stores: {
  selectedSlideTabPanel: Ref<SlideTabPanelInfo | undefined>;
  slideDetailHistory: Ref<SlideTabPanelInfo[]>;
}) => {
  const isSplitOpened = ref(false);
  const isSplitDetailLoading = ref(false);

  const splitDetailComp = shallowRef();

  const slideSplitTabPanel = ref<(SlideTabPanelInfo | CustomSlideTabPanelInfo)[]>([]);
  const splitSelectedSlideTabPanel = ref<SlideTabPanelInfo | CustomSlideTabPanelInfo>();

  const initSplitTabPanel = () => {
    isSplitOpened.value = false;
    isSplitDetailLoading.value = false;
    splitDetailComp.value = undefined;
    splitSelectedSlideTabPanel.value = undefined;
    slideSplitTabPanel.value = [];
  };

  const setSplitOpened = (value: boolean) => {
    isSplitOpened.value = value;
    if (!value) {
      initSplitTabPanel();
    }
  };

  const { isDetailSlidePermissionDenied } = useDetailSlidePermission();

  const setSelectedSlideTabPanel = (tabPanelInfo: SlideTabPanelInfo | CustomSlideTabPanelInfo) => {
    splitSelectedSlideTabPanel.value = tabPanelInfo;
    slideSplitTabPanel.value = [splitSelectedSlideTabPanel.value];
  };

  const addSplitTabPanel = <T extends {} = {}>(
    tabPanelInfo:
      | AddTabPanelInfo
      | {
          type: typeof TOTAL_DETAIL.CUSTOM;
          comp: Component | string;
          props?: T;
        },
  ) => {
    const { type } = tabPanelInfo;

    if (type === TOTAL_DETAIL.CUSTOM) {
      // @ts-expect-error TODO: FIXME
      const { comp, props } = tabPanelInfo;
      setSelectedSlideTabPanel({
        type,
        props,
      } as CustomSlideTabPanelInfo);
      splitDetailComp.value = comp;
      setSplitOpened(true);
      return;
    }

    const { rolePermissionKey } = tabPanelInfo as AddTabPanelInfo;

    if (isDetailSlidePermissionDenied({ rolePermissionKey, type })) {
      return;
    }

    if (stores.selectedSlideTabPanel.value) {
      stores.selectedSlideTabPanel.value.splitInfo = tabPanelInfo as AddTabPanelInfo;

      const currentHistory = stores.slideDetailHistory.value.find(
        (h) => h.value === stores.selectedSlideTabPanel.value?.value,
      );
      if (currentHistory) {
        currentHistory.splitInfo = tabPanelInfo as AddTabPanelInfo;
      }
    }

    setSelectedSlideTabPanel({
      ...tabPanelInfo,
      value: Date.now(),
      date: getFormattedTzTimeNow('YYYY-MM-DD HH:mm:ss'),
      state: {
        ...(tabPanelInfo.state || {}),
      },
    } as SlideTabPanelInfo);
    splitDetailComp.value = DETAIL_COMPONENTS[type].component;
    setSplitOpened(true);
  };

  const setIsSplitDetailLoading = (isLoading: boolean) => {
    isSplitDetailLoading.value = isLoading;
  };

  const selectedSplitTabComputed = (initialTab: string = '') =>
    computed({
      get: () =>
        (splitSelectedSlideTabPanel.value as SlideTabPanelInfo)?.state?.selectedTab ?? initialTab,
      set: (value) => {
        const typedSplitSelectedSlideTabPanel =
          splitSelectedSlideTabPanel.value as SlideTabPanelInfo;
        if (value === typedSplitSelectedSlideTabPanel?.state?.selectedTab) {
          return;
        }

        if (splitSelectedSlideTabPanel.value) {
          typedSplitSelectedSlideTabPanel.state = {
            ...typedSplitSelectedSlideTabPanel.state,
            selectedTab: value,
          };
        }
      },
    });

  return {
    isSplitOpened,
    splitDetailComp,
    slideSplitTabPanel,
    splitSelectedSlideTabPanel,
    isSplitDetailLoading,
    selectedSplitTabComputed,

    setSplitOpened,
    addSplitTabPanel,
    initSplitTabPanel,
    setIsSplitDetailLoading,
  };
};

export const getUniqueId = (panel: AddTabPanelInfo): string => {
  switch (panel.type) {
    case TOTAL_DETAIL.INSTANCE:
    case TOTAL_DETAIL.SHARDED_CLUSTER:
      return panel.instance?.instanceId || '';
    case TOTAL_DETAIL.CLUSTER:
      return panel.cluster?.instanceId || '';
    case TOTAL_DETAIL.OBJECT:
      return `${panel.addition?.objectName}-${panel.addition?.instanceId}`;
    case TOTAL_DETAIL.SERVICE_LIST:
      return panel.addition?.serviceId || '';
    case TOTAL_DETAIL.WAS:
      return panel.was?.wasId || '';
    case TOTAL_DETAIL.WEB_SERVER:
      return panel.webServer?.wsmId || '';
    case TOTAL_DETAIL.ALERT:
      return `${panel.addition?.alertRuleId}-${panel.addition?.alertRuleName}`;
    case TOTAL_DETAIL.EVENT_ALERT:
      return panel.addition?.resultId || '';
    case TOTAL_DETAIL.TRANSACTION:
      return panel.transaction?.tid || '';
    case TOTAL_DETAIL.TRANSACTION_INFO:
      return panel.transaction?.tid || '';
    case TOTAL_DETAIL.ACTIVE_TRANSACTION:
      return (
        panel.addition?.targetId || panel.addition?.subKey || panel.addition?.selectedTargetId || ''
      );
    case TOTAL_DETAIL.WEB_APPLICATION:
      return panel.application?.applicationId || '';
    case TOTAL_DETAIL.WEB_APPLICATION_PAGE_ANALYSIS:
      return `${panel.page?.applicationId}-${panel.page?.sessionId}-${panel.page?.pageAccessTime}-${panel.page?.pageUrl}`;
    case TOTAL_DETAIL.AJAX_REQUEST_HISTORY:
      return `${panel.addition?.applicationId}-${panel.addition?.sessionId}-${panel.addition?.callTime}-${panel.addition?.pageUrl}-${panel.addition?.pageAccessTime}-${panel.addition?.ajaxUrl}-${panel.addition?.dataId}`;
    case TOTAL_DETAIL.PAGE_PERFORMANCE_MONITORING:
      return panel.addition?.applicationId || '';
    case TOTAL_DETAIL.USER_MONITORING_SESSION_ANALYSIS:
      return `${panel.addition?.applicationId}-${panel.addition?.sessionId}-${panel.addition?.collectTime}`;
    case TOTAL_DETAIL.USER_MONITORING_SESSION_REPLAY:
      return `${panel.addition?.applicationId}-${panel.addition?.sessionId}-${panel.addition?.collectTime}`;
    case TOTAL_DETAIL.USER_MONITORING_USER_EVENT:
      return `${panel.addition?.applicationId}-${panel.addition?.sessionId}-${panel.addition?.collectTime}-${panel.addition?.pageUrl}-${panel.addition?.target}`;
    case TOTAL_DETAIL.URL_MONITORING:
      return panel.addition?.applicationId || '';
    case TOTAL_DETAIL.BROWSER_ERROR_TRACKING:
      return `${panel.addition?.applicationId}-${panel.addition?.sessionId}-${panel.addition?.collectTime}-${panel.addition?.pageAccessTime}-${panel.addition?.error}`;
    case TOTAL_DETAIL.CLOUD_ACCOUNT:
      return panel.addition?.accountId || '';
    case TOTAL_DETAIL.AGENT:
      return panel.addition?.agentId || '';
    case TOTAL_DETAIL.EXECUTION:
      return panel.addition?.executionId || '';
    case TOTAL_DETAIL.SESSION:
      return `${panel.session?.dbType}-${panel.name}`;
    case TOTAL_DETAIL.PARAMETER:
      return `${panel.addition?.dbType}-${panel.addition?.instanceId}-${panel.name}`;
    case TOTAL_DETAIL.GPU_DETAIL:
      // 한 호스트에 GPU 여러 개 → host만으로는 충돌(다른 GPU 클릭 시 토글 닫힘). GPU 식별자 포함.
      // gpuUid: gpuView 어댑터 / uuid: 호스트 GPU 그리드 어댑터 / gpuIndex: 인덱스 fallback.
      return `${panel.host}-${panel.addition?.gpuUid ?? panel.addition?.uuid ?? panel.addition?.gpuIndex ?? ''}`;
    case TOTAL_DETAIL.DISK_DETAIL:
    case TOTAL_DETAIL.NETWORK_DETAIL:
    case TOTAL_DETAIL.HOST:
      return panel.host || '';
    case TOTAL_DETAIL.PROCESS_DETAIL:
      return `${panel.host}-${panel.pid}`;
    // case TOTAL_DETAIL.SQL:
    //   return panel.name || '';
    case TOTAL_DETAIL.NETWORK_DEVICE:
      return panel.addition?.deviceId || '';
    case TOTAL_DETAIL.KAFKA_CLUSTER:
    case TOTAL_DETAIL.KAFKA_BROKER:
    case TOTAL_DETAIL.KAFKA_TOPIC:
      return panel.addition?.instanceId || '';
    case TOTAL_DETAIL.CLOUD_COMPUTING:
    case TOTAL_DETAIL.CLOUD_STORAGE:
    case TOTAL_DETAIL.CLOUD_DATABASE:
    case TOTAL_DETAIL.AWS_EC2:
    case TOTAL_DETAIL.AWS_REDSHIFT:
    case TOTAL_DETAIL.AWS_LAMBDA:
    case TOTAL_DETAIL.AWS_DOCUMENTDB:
    case TOTAL_DETAIL.AWS_S3:
    case TOTAL_DETAIL.AWS_DYNAMODB:
    case TOTAL_DETAIL.AWS_ELASTICACHE_CLUSTER:
    case TOTAL_DETAIL.AWS_ELASTICACHE_SHARD:
    case TOTAL_DETAIL.AWS_ELASTICACHE_NODE:
    case TOTAL_DETAIL.AWS_ELASTICACHE_SERVERLESS:
    case TOTAL_DETAIL.AWS_RDS:
    case TOTAL_DETAIL.AWS_DMS_TASK:
    case TOTAL_DETAIL.AWS_DMS_REPLINSTANCE:
      return panel.addition?.resourceId || '';
    case TOTAL_DETAIL.SLOW_QUERY:
      return panel.addition?.instanceId || '';
    case TOTAL_DETAIL.ACTIVE_QUERY:
      return `${panel.addition?.instanceId || ''}-${panel.addition?.activityName}`;
    case TOTAL_DETAIL.REPLICATION_OBJECT:
      return `${panel.addition?.instanceId}-${panel.addition?.objectName}`;
    case TOTAL_DETAIL.NETWORK_OBJECT:
      return panel.addition?.networkObjectId || '';
    case TOTAL_DETAIL.SCRIPT_DETAIL:
      return panel.addition?.executionId || '';
    case TOTAL_DETAIL.QUERY:
      return panel.addition?.historyId || '';
    case TOTAL_DETAIL.LOG:
      return `${panel.addition?.id}-${panel.addition?.index}`;
    case TOTAL_DETAIL.DETAILED_LOG_ANALYSIS:
      return `${panel.addition?.collectTimeUTC}-${panel.addition?.templateHash}-${panel.addition?.targetId}`;
    case TOTAL_DETAIL.KEYWORD_LOG:
      return panel.addition?.index || '';
    case TOTAL_DETAIL.NODE_LIST:
    case TOTAL_DETAIL.NAMESPACE_LIST:
    case TOTAL_DETAIL.POD_LIST:
    case TOTAL_DETAIL.SERVICE_OBJECT_LIST:
    case TOTAL_DETAIL.INGRESS_LIST:
    case TOTAL_DETAIL.PERSISTENT_VOLUMES_CLAIMS_LIST:
    case TOTAL_DETAIL.PERSISTENT_VOLUMES_LIST:
    case TOTAL_DETAIL.STORAGE_CLASSES_LIST:
    case TOTAL_DETAIL.NAMESPACE:
    case TOTAL_DETAIL.POD:
    case TOTAL_DETAIL.NODE:
    case TOTAL_DETAIL.CRON_JOB:
    case TOTAL_DETAIL.OWNER_REFERENCES:
    case TOTAL_DETAIL.DEPLOYMENT:
    case TOTAL_DETAIL.STATEFUL_SET:
    case TOTAL_DETAIL.DAEMON_SET:
    case TOTAL_DETAIL.HPA:
    case TOTAL_DETAIL.JOB:
    case TOTAL_DETAIL.REPLICA_SET:
    case TOTAL_DETAIL.SERVICE:
    case TOTAL_DETAIL.ROLES_BINDINGS:
    case TOTAL_DETAIL.SERVICE_ACCOUNTS:
    case TOTAL_DETAIL.ROLES:
    case TOTAL_DETAIL.INGRESS:
    case TOTAL_DETAIL.OPENSHIFT_ROUTE:
    case TOTAL_DETAIL.CONFIG_MAPS:
    case TOTAL_DETAIL.PERSISTENT_VOLUMES_CLAIMS:
    case TOTAL_DETAIL.STORAGE_CLASSES:
    case TOTAL_DETAIL.CLUSTER_ROLES:
    case TOTAL_DETAIL.CLUSTER_ROLES_BINDINGS:
    case TOTAL_DETAIL.PERSISTENT_VOLUMES:
    case TOTAL_DETAIL.RESOURCE_INVENTORY_SERVICE:
    case TOTAL_DETAIL.RESOURCE_INVENTORY_INGRESS:
    case TOTAL_DETAIL.RESOURCE_INVENTORY_ROUTE:
    case TOTAL_DETAIL.RESOURCE_INVENTORY_VIRTUAL_SERVICE:
      return panel.addition?.uid || '';
    case TOTAL_DETAIL.ALERT_HISTORY:
      return `${panel.addition?.fromTime}-${panel.addition?.toTime}`;
    case TOTAL_DETAIL.DATA_COLLECTION:
      return `${panel.addition.category.cCategory}-${panel.addition.id}`;
    case TOTAL_DETAIL.EVENT:
      return `${panel.addition?.eventId}-${panel.addition?.eventTime}`;
    case TOTAL_DETAIL.HISTORY_MANAGEMENT:
      return `${panel.addition?.clusterId}-${panel.addition?.kind}-${panel.addition?.uid}`;
    case TOTAL_DETAIL.EOL_EOS_POLICY:
      return panel.addition?.policyId || '';
    case TOTAL_DETAIL.K8S_CONTAINER:
    case TOTAL_DETAIL.CONTAINER_HOST:
      return panel.container || '';
    default:
      return '';
  }
};

export const useSlideDetail = () => {
  const isOpened = ref(false);
  const isDetailLoading = ref(false);

  const detailComp = shallowRef();

  const slideTabPanel = ref<SlideTabPanelInfo[]>([]);
  const selectedSlideTabPanel = ref<SlideTabPanelInfo>();
  const slideDetailHistory = ref<SlideTabPanelInfo[]>([]); // history용

  const {
    isSplitOpened,
    splitDetailComp,
    slideSplitTabPanel,
    splitSelectedSlideTabPanel,
    isSplitDetailLoading,
    selectedSplitTabComputed,

    setSplitOpened,
    addSplitTabPanel,
    initSplitTabPanel,
    setIsSplitDetailLoading,
  } = useSplitSlideDetail({
    selectedSlideTabPanel,
    slideDetailHistory,
  });

  const isExcess = ref<boolean>(false);

  const onClickExcess = () => {
    isExcess.value = false;
  };

  const { isDetailSlidePermissionDenied } = useDetailSlidePermission();

  const setDetail = (type: string) => {
    detailComp.value = DETAIL_COMPONENTS[type].component;
  };

  const initTabPanel = () => {
    slideTabPanel.value = [];
    slideDetailHistory.value = [];
    selectedSlideTabPanel.value = undefined;
    initSplitTabPanel();
  };

  const setSelectedSlideTabPanel = (tabPanelInfo: SlideTabPanelInfo) => {
    initSplitTabPanel();

    const isClickFromMainView =
      tabPanelInfo.init && tabPanelInfo.type === selectedSlideTabPanel.value?.type;

    selectedSlideTabPanel.value = {
      ...tabPanelInfo,
      state: isClickFromMainView ? (selectedSlideTabPanel.value?.state ?? {}) : tabPanelInfo.state,
    };

    if (tabPanelInfo.splitInfo) {
      const today = getTzTimeNow();
      splitSelectedSlideTabPanel.value = {
        ...tabPanelInfo.splitInfo,
        value: today.valueOf(),
        date: today.format('YYYY-MM-DD HH:mm:ss'),
      } as SlideTabPanelInfo;
      splitDetailComp.value = DETAIL_COMPONENTS[tabPanelInfo.splitInfo.type].component;
      setSplitOpened(true);
    } else {
      initSplitTabPanel();
    }

    setDetail(tabPanelInfo.type);
  };

  const addSlideDetailHistory = (tabPanelInfo: SlideTabPanelInfo) => {
    slideDetailHistory.value.push(tabPanelInfo);
    setSelectedSlideTabPanel(tabPanelInfo);
  };

  const hasSamePanel = (tabPanelInfo: SlideTabPanelInfo) => {
    const compareInfo = omit(tabPanelInfo, ['date', 'value', 'state']);
    return slideTabPanel.value.findIndex((item) => {
      const itemCompareInfo = omit(item, ['date', 'value', 'state']);
      return isEqual(compareInfo, itemCompareInfo);
    });
  };

  const deleteSlideTabPanel = (tabPanelInfo: SlideTabPanelInfo) => {
    const samePanelIndex = hasSamePanel(tabPanelInfo);
    if (samePanelIndex !== -1) {
      slideTabPanel.value.splice(samePanelIndex, 1);
    }
  };

  const addSlideTabPanel = (tabPanelInfo: SlideTabPanelInfo) => {
    if (hasSamePanel(tabPanelInfo) !== -1) {
      deleteSlideTabPanel(tabPanelInfo);
    }
    slideTabPanel.value.splice(1, 0, tabPanelInfo);
  };

  const deleteLastSlideDetailHistory = () => {
    return slideDetailHistory.value.pop();
  };

  const setOpened = (value: boolean) => {
    isOpened.value = value;
    if (!value) {
      initTabPanel();
    }
  };

  const isOnlyDifferentSelectedTab = (tabPanelInfo: AddTabPanelInfo) => {
    if (!selectedSlideTabPanel.value) {
      return false;
    }

    const selectedUniqueId = getUniqueId(selectedSlideTabPanel.value);
    const targetUniqueId = getUniqueId(tabPanelInfo);

    if (
      selectedSlideTabPanel.value.type !== tabPanelInfo.type ||
      selectedUniqueId !== targetUniqueId
    ) {
      return false;
    }

    const omitSelectedSlideTabPanel = omit(selectedSlideTabPanel.value, ['date', 'value', 'state']);
    const omitTabPanelInfo = omit(tabPanelInfo, ['date', 'value', 'state']);

    return (
      isEqual(omitSelectedSlideTabPanel, omitTabPanelInfo) &&
      tabPanelInfo.state?.selectedTab !== selectedSlideTabPanel.value?.state?.selectedTab
    );
  };

  const updateSelectedTabState = (tabPanelInfo: AddTabPanelInfo) => {
    if (!selectedSlideTabPanel.value) {
      return;
    }

    const nextState = {
      ...(selectedSlideTabPanel.value.state ?? {}),
      ...(tabPanelInfo.state ?? {}),
    };

    selectedSlideTabPanel.value = {
      ...selectedSlideTabPanel.value,
      state: nextState,
    };

    const targetValue = selectedSlideTabPanel.value.value;
    const clonedState = cloneDeep(nextState);

    const panelIndex = slideTabPanel.value.findIndex((panel) => panel.value === targetValue);
    if (panelIndex !== -1) {
      slideTabPanel.value.splice(panelIndex, 1, {
        ...slideTabPanel.value[panelIndex],
        state: clonedState,
      });
    }

    const historyIndex = slideDetailHistory.value.findIndex((panel) => panel.value === targetValue);
    if (historyIndex !== -1) {
      slideDetailHistory.value.splice(historyIndex, 1, {
        ...slideDetailHistory.value[historyIndex],
        state: cloneDeep(clonedState),
      });
    }
  };

  const addTabPanel = (
    tabPanelInfo: AddTabPanelInfo,
    currentState?: SlideTabPanelInfo['state'],
  ): SlideDetailAdapterResultKey => {
    const { type, init, rolePermissionKey } = tabPanelInfo;
    if (isDetailSlidePermissionDenied({ rolePermissionKey, type })) {
      return 'permission_denied';
    }

    if (currentState?.openSlide !== false && type !== TOTAL_DETAIL.SQL) {
      const newPanelId = getUniqueId(tabPanelInfo);

      if (
        selectedSlideTabPanel.value &&
        selectedSlideTabPanel.value.type === type &&
        getUniqueId(selectedSlideTabPanel.value) === newPanelId &&
        isOpened.value &&
        currentState?.keepOpen !== true
      ) {
        if (isOnlyDifferentSelectedTab(tabPanelInfo) && currentState?.noKeepState === true) {
          updateSelectedTabState(tabPanelInfo);
          return 'ok';
        }

        setOpened(false);
        return 'toggled_close';
      }
    }

    const keepState =
      selectedSlideTabPanel.value?.type === type && init && !currentState?.noKeepState;
    const beforeState = {
      ...(selectedSlideTabPanel.value?.state ?? {}),
    };

    if (init) {
      initTabPanel();
    } else if (currentState && selectedSlideTabPanel.value) {
      selectedSlideTabPanel.value.state = currentState;
    }

    if (slideTabPanel.value.length > 30) {
      isExcess.value = true;
      return 'excess';
    }

    const today = getTzTimeNow();
    const date = today.format('YYYY-MM-DD HH:mm:ss');
    const dateToValue = today.valueOf();

    const newTab = {
      ...tabPanelInfo,
      value: dateToValue,
      date,
      state: {
        ...tabPanelInfo.state,
        ...(keepState ? beforeState : {}),
      },
    };

    addSlideTabPanel(newTab);
    addSlideDetailHistory(newTab);
    if (currentState?.openSlide !== false) {
      setOpened(true);
    }
    return 'ok';
  };

  const loadingList: string[] = [];

  const setIsDetailLoading = (
    isLoading:
      | boolean
      | {
          type: string;
          isLoading: boolean;
        },
  ) => {
    if (typeof isLoading === 'object' && 'type' in isLoading && 'isLoading' in isLoading) {
      if (isLoading.isLoading) {
        loadingList.push(isLoading.type);
      } else {
        loadingList.splice(loadingList.indexOf(isLoading.type), 1);
      }
      if (loadingList.length === 0) {
        isDetailLoading.value = false;
      } else {
        isDetailLoading.value = true;
      }
    } else if (typeof isLoading === 'boolean') {
      isDetailLoading.value = isLoading;
      if (isLoading === false) {
        loadingList.splice(0, loadingList.length);
      }
    }
  };

  const setSlideDetailTitle = (title: string) => {
    if (!selectedSlideTabPanel.value) {
      return;
    }

    selectedSlideTabPanel.value.name = title;
    const foundHistory = slideDetailHistory.value.find(
      (h) => h.value === selectedSlideTabPanel.value?.value,
    );

    if (foundHistory) {
      foundHistory.name = title;
    }
  };

  /** 메인 패널 보조 제목(subTitle) 갱신 — 히스토리 항목까지 동기화. */
  const setSlideDetailSubTitle = (subTitle: string) => {
    if (!selectedSlideTabPanel.value) {
      return;
    }

    selectedSlideTabPanel.value.subTitle = subTitle;
    const foundHistory = slideDetailHistory.value.find(
      (h) => h.value === selectedSlideTabPanel.value?.value,
    );

    if (foundHistory) {
      foundHistory.subTitle = subTitle;
    }
  };

  /** 분할(split) 패널 보조 제목(subTitle) 갱신 — split 은 히스토리 스택이 없어 선택 패널만 갱신. */
  const setSplitSlideDetailSubTitle = (subTitle: string) => {
    if (!splitSelectedSlideTabPanel.value) {
      return;
    }

    splitSelectedSlideTabPanel.value.subTitle = subTitle;
  };

  const selectedTabComputed = (initialTab: MaybeRefOrGetter<string> = '') =>
    computed({
      get: () => selectedSlideTabPanel.value?.state?.selectedTab ?? toValue(initialTab),
      set: (value) => {
        if (value === selectedSlideTabPanel.value?.state?.selectedTab) {
          return;
        }

        if (selectedSlideTabPanel.value) {
          if (!selectedSlideTabPanel.value.splitInfo?.keepSplitTab) {
            initSplitTabPanel();
          }

          selectedSlideTabPanel.value.state = {
            ...selectedSlideTabPanel.value.state,
            selectedTab: value,
          };

          const findSlideDetailHistory = slideDetailHistory.value.find(
            (h) =>
              h.type === selectedSlideTabPanel?.value?.type &&
              h.value === selectedSlideTabPanel?.value?.value,
          );
          if (findSlideDetailHistory) {
            findSlideDetailHistory.state = cloneDeep(selectedSlideTabPanel?.value?.state);
          }
        } else {
          // History를 사용하지 않고, Detail View만 사용하는 경우
          selectedSlideTabPanel.value = {
            type: TOTAL_DETAIL.CUSTOM,
            name: '',
            value: 0,
            date: '',
            state: {
              selectedTab: value,
            },
          };
        }
      },
    });

  return {
    isOpened,
    isDetailLoading,
    detailComp,
    slideTabPanel,
    selectedSlideTabPanel,
    slideDetailHistory,
    selectedTabComputed,
    setDetail,
    setSelectedSlideTabPanel,
    setSlideDetailTitle,
    setSlideDetailSubTitle,
    setSplitSlideDetailSubTitle,
    initTabPanel,
    addSlideDetailHistory,
    deleteLastSlideDetailHistory,
    addTabPanel,
    setIsDetailLoading,
    setOpened,
    deleteSlideTabPanel,
    isExcess,
    onClickExcess,

    isSplitOpened,
    splitDetailComp,
    slideSplitTabPanel,
    splitSelectedSlideTabPanel,
    isSplitDetailLoading,
    selectedSplitTabComputed,
    setSplitOpened,
    addSplitTabPanel,
    initSplitTabPanel,
    setIsSplitDetailLoading,
    isDetailSlidePermissionDenied,
  };
};
